import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useApp } from '@/components/AppLayout';
import { useBusinessNav } from '@/hooks/useBusinessNav';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import {
  ArrowLeft,
  Check,
  AlertCircle,
  RefreshCw,
  Loader2,
  FileSpreadsheet,
  FileText,
  History,
  Link2,
  Link2Off,
  Users,
  FileText as InvoiceIcon,
  CreditCard,
  Receipt,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Download,
} from 'lucide-react';
import {
  accountingApi,
  type AccountingStatus,
  type AccountingConnection,
  type SyncPreview,
  type SyncLogEntry,
} from '@/lib/api';

// QuickBooks brand colors
const QB_GREEN = '#2ca01c';

// =============================================================================
// ACCOUNTING CONNECTION CARD
// =============================================================================

interface AccountingConnectionCardProps {
  platform: 'quickbooks' | 'xero' | 'freshbooks';
  platformName: string;
  platformLogo?: React.ReactNode;
  status: AccountingStatus[keyof AccountingStatus] | null;
  connection?: AccountingConnection;
  onConnect: () => void;
  onDisconnect: () => void;
  onSync: () => void;
  onViewHistory: () => void;
  connecting: boolean;
  syncing: boolean;
}

function AccountingConnectionCard({
  platformName,
  platformLogo,
  status,
  connection,
  onConnect,
  onDisconnect,
  onSync,
  onViewHistory,
  connecting,
  syncing,
}: AccountingConnectionCardProps) {
  const isConnected = status?.status === 'active';
  const isError = status?.status === 'error' || status?.status === 'expired';

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Never';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {platformLogo}
            <div>
              <CardTitle className="text-base">{platformName}</CardTitle>
              {status?.company_name && (
                <CardDescription>{status.company_name}</CardDescription>
              )}
            </div>
          </div>
          {isConnected && (
            <div className="flex items-center gap-1.5 text-sm font-medium text-green-600">
              <Check className="h-4 w-4" />
              Connected
            </div>
          )}
          {isError && (
            <div className="flex items-center gap-1.5 text-sm font-medium text-amber-600">
              <AlertCircle className="h-4 w-4" />
              {status?.status === 'expired' ? 'Token Expired' : 'Error'}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <Separator className="mb-6" />

        {isConnected ? (
          <div className="space-y-4">
            {/* Sync Stats */}
            {connection?.sync_stats && (
              <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Synced Records</span>
                  <span className="font-medium">{connection.sync_stats.synced}</span>
                </div>
                {connection.sync_stats.pending > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Pending</span>
                    <span className="font-medium text-amber-600">
                      {connection.sync_stats.pending}
                    </span>
                  </div>
                )}
                {connection.sync_stats.error > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Errors</span>
                    <span className="font-medium text-red-600">
                      {connection.sync_stats.error}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Last Sync */}
            <div className="text-sm text-muted-foreground">
              Last synced: {formatDate(status.last_sync_at)}
            </div>

            {/* Action Buttons */}
            <div className="flex flex-wrap gap-2">
              <Button onClick={onSync} disabled={syncing}>
                {syncing ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <RefreshCw className="mr-2 h-4 w-4" />
                )}
                Sync Now
              </Button>
              <Button variant="outline" onClick={onViewHistory}>
                <History className="mr-2 h-4 w-4" />
                History
              </Button>
              <Button variant="ghost" onClick={onDisconnect} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                <Link2Off className="mr-2 h-4 w-4" />
                Disconnect
              </Button>
            </div>
          </div>
        ) : isError ? (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="h-8 w-8 rounded-full bg-amber-100 dark:bg-amber-900 flex items-center justify-center">
                <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">Reconnection Required</p>
                <p className="text-sm text-muted-foreground">
                  Your connection has expired. Please reconnect to continue syncing.
                </p>
              </div>
            </div>
            <Button onClick={onConnect} disabled={connecting}>
              {connecting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="mr-2 h-4 w-4" />
              )}
              Reconnect
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="space-y-2">
              <p className="text-sm">
                Connect to {platformName} to automatically sync your clients, invoices, and payments.
              </p>
              <ul className="text-sm text-muted-foreground space-y-1">
                <li className="flex items-center gap-2">
                  <Users className="h-3.5 w-3.5" />
                  Sync clients as customers
                </li>
                <li className="flex items-center gap-2">
                  <InvoiceIcon className="h-3.5 w-3.5" />
                  Sync invoices automatically
                </li>
                <li className="flex items-center gap-2">
                  <CreditCard className="h-3.5 w-3.5" />
                  Record payments in your books
                </li>
              </ul>
            </div>

            <Button
              onClick={onConnect}
              disabled={connecting}
              style={{ backgroundColor: QB_GREEN }}
              className="text-white hover:opacity-90"
            >
              {connecting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Link2 className="mr-2 h-4 w-4" />
              )}
              Connect to {platformName}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// SYNC PREVIEW MODAL
// =============================================================================

interface SyncPreviewModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
  onConfirm: () => void;
  syncing: boolean;
}

function SyncPreviewModal({
  open,
  onOpenChange,
  connectionId,
  onConfirm,
  syncing,
}: SyncPreviewModalProps) {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState<SyncPreview | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && connectionId) {
      loadPreview();
    }
  }, [open, connectionId]);

  const loadPreview = async () => {
    setLoading(true);
    setError('');

    const response = await accountingApi.getSyncPreview(connectionId);

    setLoading(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    if (response.data) {
      setPreview(response.data);
    }
  };

  const getEntityIcon = (type: string) => {
    switch (type) {
      case 'client':
        return <Users className="h-4 w-4" />;
      case 'invoice':
        return <InvoiceIcon className="h-4 w-4" />;
      case 'credit_memo':
        return <Receipt className="h-4 w-4" />;
      case 'payment':
        return <CreditCard className="h-4 w-4" />;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Sync Preview</DialogTitle>
          <DialogDescription>
            Review the changes that will be synced to your accounting system.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3">
            <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
          </div>
        ) : preview ? (
          <div className="space-y-4">
            {preview.total_pending === 0 ? (
              <div className="text-center py-4">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-sm text-muted-foreground">
                  Everything is up to date! No changes to sync.
                </p>
              </div>
            ) : (
              <>
                <div className="text-sm font-medium">
                  {preview.total_pending} record{preview.total_pending !== 1 ? 's' : ''} to sync
                </div>

                <div className="space-y-2">
                  {preview.summary.clients > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      {getEntityIcon('client')}
                      <span>{preview.summary.clients} client{preview.summary.clients !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {preview.summary.invoices > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      {getEntityIcon('invoice')}
                      <span>{preview.summary.invoices} invoice{preview.summary.invoices !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {preview.summary.credit_memos > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      {getEntityIcon('credit_memo')}
                      <span>{preview.summary.credit_memos} credit memo{preview.summary.credit_memos !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                  {preview.summary.payments > 0 && (
                    <div className="flex items-center gap-2 text-sm">
                      {getEntityIcon('payment')}
                      <span>{preview.summary.payments} payment{preview.summary.payments !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={syncing}>
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={syncing || loading || !preview || preview.total_pending === 0}
          >
            {syncing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Start Sync
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// SYNC HISTORY DRAWER
// =============================================================================

interface SyncHistoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  connectionId: string;
}

function SyncHistoryDrawer({ open, onOpenChange, connectionId }: SyncHistoryDrawerProps) {
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<SyncLogEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open && connectionId) {
      loadHistory();
    }
  }, [open, connectionId]);

  const loadHistory = async () => {
    setLoading(true);
    setError('');

    const response = await accountingApi.getSyncHistory(connectionId);

    setLoading(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    if (response.data) {
      setHistory(response.data.results);
      setTotal(response.data.total);
    }
  };

  const getStatusIcon = (status: SyncLogEntry['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'partial':
        return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'failed':
        return <XCircle className="h-4 w-4 text-red-500" />;
      case 'started':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: SyncLogEntry['status']) => {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'partial':
        return 'Partial';
      case 'failed':
        return 'Failed';
      case 'started':
        return 'In Progress';
      default:
        return status;
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="sm:max-w-md">
        <SheetHeader>
          <SheetTitle>Sync History</SheetTitle>
          <SheetDescription>
            View past sync operations and their results.
          </SheetDescription>
        </SheetHeader>

        <ScrollArea className="flex-1 -mx-4 px-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3">
              <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
            </div>
          ) : history.length === 0 ? (
            <div className="text-center py-8">
              <History className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No sync history yet.</p>
            </div>
          ) : (
            <div className="space-y-3 py-4">
              {history.map((log) => (
                <div
                  key={log.id}
                  className="border rounded-lg p-3 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(log.status)}
                      <span className="text-sm font-medium">{getStatusLabel(log.status)}</span>
                    </div>
                    <span className="text-xs text-muted-foreground">
                      {formatDate(log.started_at)}
                    </span>
                  </div>

                  <div className="flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="text-green-600">{log.items_synced} synced</span>
                    {log.items_failed > 0 && (
                      <span className="text-red-600">{log.items_failed} failed</span>
                    )}
                    {log.items_skipped > 0 && (
                      <span>{log.items_skipped} skipped</span>
                    )}
                  </div>

                  {log.details?.errors && log.details.errors.length > 0 && (
                    <div className="mt-2 text-xs">
                      <p className="font-medium text-red-600 mb-1">Errors:</p>
                      {log.details.errors.slice(0, 3).map((err, idx) => (
                        <p key={idx} className="text-muted-foreground truncate">
                          {err.display_name}: {err.error}
                        </p>
                      ))}
                      {log.details.errors.length > 3 && (
                        <p className="text-muted-foreground">
                          +{log.details.errors.length - 3} more errors
                        </p>
                      )}
                    </div>
                  )}

                  {log.triggered_by && (
                    <p className="text-xs text-muted-foreground">
                      By: {log.triggered_by}
                    </p>
                  )}
                </div>
              ))}

              {total > history.length && (
                <p className="text-center text-xs text-muted-foreground pt-2">
                  Showing {history.length} of {total} sync operations
                </p>
              )}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}

// =============================================================================
// EXPORT SECTION
// =============================================================================

interface ExportSectionProps {
  businessId: string;
}

function ExportSection({ businessId }: ExportSectionProps) {
  const [exportingCsv, setExportingCsv] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async (format: 'csv' | 'excel') => {
    setError('');

    if (format === 'csv') {
      setExportingCsv(true);
    } else {
      setExportingExcel(true);
    }

    try {
      await accountingApi.exportData(businessId, format);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExportingCsv(false);
      setExportingExcel(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Download className="h-4 w-4" />
          Export Data
        </CardTitle>
        <CardDescription>
          Download your accounting data for manual import or backup.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Separator className="mb-6" />

        {error && (
          <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3 mb-4">
            <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
          </div>
        )}

        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Export all clients, invoices, credit memos, and payments.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => handleExport('excel')}
              disabled={exportingCsv || exportingExcel}
            >
              {exportingExcel ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileSpreadsheet className="mr-2 h-4 w-4" />
              )}
              Download Excel
            </Button>
            <Button
              variant="outline"
              onClick={() => handleExport('csv')}
              disabled={exportingCsv || exportingExcel}
            >
              {exportingCsv ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <FileText className="mr-2 h-4 w-4" />
              )}
              Download CSV
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// =============================================================================
// AUTO SYNC SETTINGS
// =============================================================================

interface AutoSyncSettingsProps {
  connection: AccountingConnection | null;
  onToggle: (enabled: boolean) => void;
  onAccountingMethodChange: (method: 'cash' | 'accrual') => void;
  saving: boolean;
}

function AutoSyncSettings({ connection, onToggle, onAccountingMethodChange, saving }: AutoSyncSettingsProps) {
  if (!connection) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Sync Settings</CardTitle>
        <CardDescription>Configure sync and accounting preferences.</CardDescription>
      </CardHeader>
      <CardContent>
        <Separator className="mb-6" />

        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="auto-sync-toggle" className="text-sm font-medium">
                Auto-sync
              </Label>
              <p className="text-sm text-muted-foreground">
                Automatically sync changes to your accounting system.
              </p>
            </div>
            <Switch
              id="auto-sync-toggle"
              checked={connection.auto_sync_enabled}
              onCheckedChange={onToggle}
              disabled={saving}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="accounting-method" className="text-sm font-medium">
                Default Accounting Method
              </Label>
              <p className="text-sm text-muted-foreground">
                Sets the default view for tax reports.
              </p>
            </div>
            <Select
              value={connection.accounting_method}
              onValueChange={(value) => onAccountingMethodChange(value as 'cash' | 'accrual')}
              disabled={saving}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="accrual">Accrual</SelectItem>
                <SelectItem value="cash">Cash</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {saving && (
          <div className="flex items-center gap-2 mt-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Saving...
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

function AccountingSyncContent() {
  const { nav } = useBusinessNav();
  const [searchParams] = useSearchParams();
  const { currentBusiness } = useApp();

  // State
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<AccountingStatus | null>(null);
  const [connections, setConnections] = useState<AccountingConnection[]>([]);
  const [connecting, setConnecting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [savingAutoSync, setSavingAutoSync] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Modals/Drawers
  const [showSyncPreview, setShowSyncPreview] = useState(false);
  const [showSyncHistory, setShowSyncHistory] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);

  // Load data on mount
  useEffect(() => {
    if (currentBusiness) {
      loadData();
    }
  }, [currentBusiness]);

  // Handle OAuth callback
  useEffect(() => {
    const connected = searchParams.get('connected');
    if (connected === 'quickbooks') {
      setSuccessMessage('Successfully connected to QuickBooks!');
      // Clear the URL parameter
      nav('/settings/accounting', { replace: true });
      // Reload data
      if (currentBusiness) {
        loadData();
      }
    }
  }, [searchParams, currentBusiness, nav]);

  const loadData = async () => {
    if (!currentBusiness) return;

    setLoading(true);
    setError('');

    // Load status and connections in parallel
    const [statusRes, connectionsRes] = await Promise.all([
      accountingApi.getStatus(currentBusiness.id),
      accountingApi.listConnections(),
    ]);

    setLoading(false);

    if (statusRes.error) {
      setError(statusRes.error);
      return;
    }

    if (statusRes.data) {
      setStatus(statusRes.data);
    }

    if (connectionsRes.data) {
      setConnections(connectionsRes.data);
    }
  };

  const handleQuickBooksConnect = async () => {
    if (!currentBusiness) return;

    setConnecting(true);
    setError('');

    const response = await accountingApi.quickbooksConnect(currentBusiness.id);

    if (response.error) {
      setError(response.error);
      setConnecting(false);
      return;
    }

    if (response.data?.auth_url) {
      // Redirect to QuickBooks authorization
      window.location.href = response.data.auth_url;
    }
  };

  const handleQuickBooksDisconnect = async () => {
    if (!currentBusiness) return;

    if (!confirm('Are you sure you want to disconnect QuickBooks? This will stop all syncing.')) {
      return;
    }

    setConnecting(true);
    setError('');

    const response = await accountingApi.quickbooksDisconnect(currentBusiness.id);

    setConnecting(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    setSuccessMessage('QuickBooks disconnected successfully.');
    loadData();
  };

  const handleOpenSyncPreview = (connectionId: string) => {
    setSelectedConnectionId(connectionId);
    setShowSyncPreview(true);
  };

  const handleExecuteSync = async () => {
    if (!selectedConnectionId) return;

    setSyncing(true);
    setError('');

    const response = await accountingApi.executeSync(selectedConnectionId);

    setSyncing(false);
    setShowSyncPreview(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    if (response.data) {
      const { items_synced, items_failed } = response.data;
      if (items_failed > 0) {
        setSuccessMessage(`Sync completed: ${items_synced} synced, ${items_failed} failed.`);
      } else {
        setSuccessMessage(`Sync completed: ${items_synced} records synced successfully!`);
      }
    }

    loadData();
  };

  const handleViewHistory = (connectionId: string) => {
    setSelectedConnectionId(connectionId);
    setShowSyncHistory(true);
  };

  const handleToggleAutoSync = async (enabled: boolean) => {
    const connection = connections.find((c) => c.platform === 'quickbooks');
    if (!connection) return;

    setSavingAutoSync(true);

    const response = await accountingApi.updateConnection(connection.id, {
      auto_sync_enabled: enabled,
    });

    setSavingAutoSync(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    // Update local state
    setConnections((prev) =>
      prev.map((c) =>
        c.id === connection.id ? { ...c, auto_sync_enabled: enabled } : c
      )
    );
  };

  const handleAccountingMethodChange = async (method: 'cash' | 'accrual') => {
    const connection = connections.find((c) => c.platform === 'quickbooks');
    if (!connection) return;

    setSavingAutoSync(true);

    const response = await accountingApi.updateConnection(connection.id, {
      accounting_method: method,
    });

    setSavingAutoSync(false);

    if (response.error) {
      setError(response.error);
      return;
    }

    // Update local state
    setConnections((prev) =>
      prev.map((c) =>
        c.id === connection.id ? { ...c, accounting_method: method } : c
      )
    );
  };

  // Dismiss messages after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  if (!currentBusiness) {
    return null;
  }

  const quickbooksConnection = connections.find((c) => c.platform === 'quickbooks');

  return (
    <div className="px-6 pt-6 pb-6">
      <div className="space-y-1 mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-2 -ml-2 text-muted-foreground"
          onClick={() => nav('/settings')}
        >
          <ArrowLeft className="mr-1 h-4 w-4" />
          Back to Settings
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Accounting Sync</h1>
        <p className="text-sm text-muted-foreground">
          Connect to your accounting software to keep your books in sync.
        </p>
      </div>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-md p-3 mb-6">
          <p className="text-sm text-green-900 dark:text-green-100 flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            {successMessage}
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-md p-3 mb-6">
          <p className="text-sm text-red-900 dark:text-red-100">{error}</p>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <div className="space-y-6">
          {/* QuickBooks Connection */}
          <AccountingConnectionCard
            platform="quickbooks"
            platformName="QuickBooks Online"
            platformLogo={
              <div
                className="h-10 w-10 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: QB_GREEN }}
              >
                QB
              </div>
            }
            status={status?.quickbooks || null}
            connection={quickbooksConnection}
            onConnect={handleQuickBooksConnect}
            onDisconnect={handleQuickBooksDisconnect}
            onSync={() => quickbooksConnection && handleOpenSyncPreview(quickbooksConnection.id)}
            onViewHistory={() => quickbooksConnection && handleViewHistory(quickbooksConnection.id)}
            connecting={connecting}
            syncing={syncing}
          />

          {/* Auto Sync Settings - only show when connected */}
          {quickbooksConnection && status?.quickbooks?.status === 'active' && (
            <AutoSyncSettings
              connection={quickbooksConnection}
              onToggle={handleToggleAutoSync}
              onAccountingMethodChange={handleAccountingMethodChange}
              saving={savingAutoSync}
            />
          )}

          {/* Export Section */}
          <ExportSection businessId={currentBusiness.id} />

          {/* Coming Soon - Other Platforms */}
          <Card className="opacity-60">
            <CardHeader>
              <CardTitle className="text-base">More Integrations Coming Soon</CardTitle>
              <CardDescription>
                Support for Xero, FreshBooks, and other accounting platforms is on the way.
              </CardDescription>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Sync Preview Modal */}
      {selectedConnectionId && (
        <SyncPreviewModal
          open={showSyncPreview}
          onOpenChange={setShowSyncPreview}
          connectionId={selectedConnectionId}
          onConfirm={handleExecuteSync}
          syncing={syncing}
        />
      )}

      {/* Sync History Drawer */}
      {selectedConnectionId && (
        <SyncHistoryDrawer
          open={showSyncHistory}
          onOpenChange={setShowSyncHistory}
          connectionId={selectedConnectionId}
        />
      )}
    </div>
  );
}

export function AccountingSync() {
  return <AccountingSyncContent />;
}
